import styled, { createGlobalStyle, keyframes } from 'styled-components';

import {
  BLOG_GEN_ANIM,
  BLOG_LIST,
  BRAND_DEPLOYMENT_GLOW,
  BRAND_DEPLOYMENT_GLOW_STOPS,
  COLORS,
  REDUCED_MOTION,
} from '../../../../theme/tokens';
import { Text } from '../../../../components/Text/Text';

/** Registers the animatable glow angle once (`createGlobalStyle` dedupes across the
 *  rows that render it). On engines that ignore animating a registered property the
 *  arc simply sits static, which is the same fallback the deployment strip takes. */
export const GlowAngleProperty = createGlobalStyle`
  @property --blog-gen-glow-angle {
    syntax: '<angle>';
    inherits: false;
    initial-value: 0deg;
  }
`;

/** One loop of the glow travelling around the row's edge. */
const glowSpin = keyframes`
  to {
    --blog-gen-glow-angle: 360deg;
  }
`;

// A conic gradient whose `from <angle>` is an ANIMATED CSS custom property — a
// sweeping coloured arc; cannot be a static GRADIENTS token. Same construction and
// stops as the deployment strip's in-progress card (see
// `pages/home/components/BrandDeploymentStrip/BounceCard/BounceCard.styles.ts` —
// change one and check the other), so "something is being built for you right now"
// reads identically wherever the studio says it.
const GLOW_RING = `conic-gradient(from var(--blog-gen-glow-angle), transparent 0 ${100 - BRAND_DEPLOYMENT_GLOW.ARC_PCT}%, ${BRAND_DEPLOYMENT_GLOW_STOPS[0]} ${100 - BRAND_DEPLOYMENT_GLOW.ARC_PCT * 0.62}%, ${BRAND_DEPLOYMENT_GLOW_STOPS[1]} ${100 - BRAND_DEPLOYMENT_GLOW.ARC_PCT * 0.36}%, ${BRAND_DEPLOYMENT_GLOW_STOPS[2]} ${100 - BRAND_DEPLOYMENT_GLOW.ARC_PCT * 0.09}%, transparent 100%)`; // design-allow
// Mask-composite alpha idiom that cuts the gradient down to a ring; the `#000` is a
// structural alpha source, not a colour.
const RING_MASK = 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)'; // design-allow

/**
 * The travelling border of the row an article is being written into.
 *
 * It renders inside the first cell but anchors to the `<tr>` (the blog list's
 * `TablePanelBody` makes a row carrying `data-generating` the containing block), so
 * `inset: 0` traces the WHOLE row rather than the title cell. Only the rim is
 * painted (a conic gradient cut down by the mask-composite idiom), so the row keeps
 * the table's own surface and stays legible in both themes.
 *
 * Above the cells (`z-index: 1`) so their bottom hairline cannot cut through the rim,
 * and `pointer-events: none` so it never answers a hit-test over the row's content.
 * The negative delay staggers rows started together, so two of them never sweep in
 * lockstep. Holds still under reduced motion.
 */
export const GlowRing = styled.span<{ $index: number }>`
  position: absolute;
  inset: 0;
  z-index: 1;
  border-radius: 10px;
  padding: 1.5px;
  background: ${GLOW_RING};
  -webkit-mask: ${RING_MASK};
  mask: ${RING_MASK};
  -webkit-mask-composite: xor;
  mask-composite: exclude;
  filter: blur(2.5px);
  opacity: ${BRAND_DEPLOYMENT_GLOW.ALPHA};
  pointer-events: none;
  animation: ${glowSpin} ${BRAND_DEPLOYMENT_GLOW.SPIN_MS}ms linear infinite;
  animation-delay: ${({ $index }) => `-${($index % 4) * 420}ms`};

  ${REDUCED_MOTION} {
    animation: none;
  }
`;

/** Title cell of a generating row: the cover frame + the status stack beside it. */
export const Cell = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
`;

/**
 * The cover slot, left empty while the article is written. It keeps exactly the
 * geometry of the list's hero thumb, so nothing in the row moves when the real
 * cover replaces it.
 */
export const ThumbFrame = styled.div`
  width: 64px;
  aspect-ratio: 16 / 9;
  flex: 0 0 auto;
  border-radius: 8px;
  background: ${COLORS.surfaceFaint};
  /* An INSET hairline, not a border: a border would add its 2px to the box and
     shift the title by that much when the real 64px thumb replaces this. */
  box-shadow: inset 0 0 0 1px ${COLORS.hairline};
`;

export const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
`;

/** The self-typing status caption, in the slot the article title will take. */
export const Caption = styled(Text)<{ $visible: boolean }>`
  font-size: 14px;
  font-weight: 600;
  color: ${COLORS.text};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity ${BLOG_GEN_ANIM.fadeMs}ms ease;
`;

/** What the owner asked for, under the caption (where the slug will be). */
export const Topic = styled(Text)`
  font-size: 12px;
  color: ${COLORS.textFaint};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const breathe = keyframes`
  0%, 100% { opacity: 0.5; }
  50% { opacity: 1; }
`;

/**
 * Stand-in for the columns the article does not have yet (category, updated).
 * A short breathing bar rather than an em dash, so the row reads as unfinished
 * instead of as empty. Holds still under reduced motion.
 */
export const PendingBar = styled.span`
  display: block;
  width: 48px;
  height: 8px;
  border-radius: 99px;
  background: ${COLORS.surfaceMuted};
  animation: ${breathe} ${BLOG_GEN_ANIM.pendingBreatheMs}ms ease-in-out infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    opacity: 0.7;
  }
`;

/** The row's status pill, in place of the Draft / Published badge. It borrows the
 *  list's own pillar-pill treatment, so the two pills in one table read as a set. */
export const WritingPill = styled(Text)`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 99px;
  background: ${BLOG_LIST.pillBg};
  color: ${BLOG_LIST.pillText};
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  white-space: nowrap;
`;
